/**
 * AppearanceProvider - React Context for managing appearance settings
 * 
 * Features:
 * - Per-component appearance settings
 * - localStorage persistence with configurable key
 * - CSS variable injection for live updates
 * - System theme detection support
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
    AppearanceConfig,
    ComponentAppearance,
    DEFAULT_APPEARANCE,
    FONT_FAMILIES,
    FONT_SIZES,
    ACCENT_COLORS,
    Theme,
    FontSize,
    FontFamily,
    AccentColor,
} from './types';

interface AppearanceContextType {
    // Full config
    config: AppearanceConfig;

    // Get effective settings for a component (merges global + component-specific)
    getComponentAppearance: (componentId: string) => ComponentAppearance;

    // Update settings
    updateGlobal: (settings: Partial<ComponentAppearance>) => void;
    updateComponent: (componentId: string, settings: Partial<ComponentAppearance>) => void;
    resetComponent: (componentId: string) => void;
    resetAll: () => void;

    // Convenience getters for global settings
    theme: Theme;
    fontSize: FontSize;
    fontFamily: FontFamily;
    accentColor: AccentColor;

    // Convenience setters
    setTheme: (theme: Theme) => void;
    setFontSize: (size: FontSize) => void;
    setFontFamily: (family: FontFamily) => void;
    setAccentColor: (color: AccentColor) => void;

    // Resolved theme (handles 'system' preference)
    resolvedTheme: 'light' | 'dark';
}

const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

interface AppearanceProviderProps {
    children: React.ReactNode;
    /** Storage key prefix for localStorage. Default: 'appearance' */
    storageKey?: string;
    /** Initial config to use if nothing in storage */
    defaultConfig?: Partial<AppearanceConfig>;
}

export function AppearanceProvider({
    children,
    storageKey = 'appearance',
    defaultConfig
}: AppearanceProviderProps) {
    // Initialize from localStorage or defaults
    const [config, setConfig] = useState<AppearanceConfig>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                return { ...DEFAULT_APPEARANCE, ...parsed };
            }
        } catch (e) {
            console.warn('Failed to load appearance settings:', e);
        }
        return { ...DEFAULT_APPEARANCE, ...defaultConfig };
    });

    // System theme preference
    const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    });

    // Listen for system theme changes
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    // Persist to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(config));
        } catch (e) {
            console.warn('Failed to save appearance settings:', e);
        }
    }, [config, storageKey]);

    // Resolved theme
    const resolvedTheme: 'light' | 'dark' =
        config.global.theme === 'system' ? systemTheme : (config.global.theme || 'light');

    // Apply CSS variables to document
    useEffect(() => {
        const root = document.documentElement;

        // Theme class
        root.classList.remove('light', 'dark');
        root.classList.add(resolvedTheme);

        // Font size
        const fontSizeConfig = FONT_SIZES.find(f => f.value === config.global.fontSize);
        if (fontSizeConfig) {
            root.style.fontSize = fontSizeConfig.css;
        }

        // Font family
        const fontFamilyConfig = FONT_FAMILIES.find(f => f.value === config.global.fontFamily);
        if (fontFamilyConfig) {
            root.style.fontFamily = fontFamilyConfig.css;
        }

        // Accent color as CSS variable
        const accentConfig = ACCENT_COLORS.find(c => c.value === config.global.accentColor);
        if (accentConfig) {
            root.style.setProperty('--accent-color', accentConfig.color);
        }
    }, [config, resolvedTheme]);

    // Get effective appearance for a component
    const getComponentAppearance = useCallback((componentId: string): ComponentAppearance => {
        const componentSettings = config.components[componentId] || {};
        return { ...config.global, ...componentSettings };
    }, [config]);

    // Update global settings
    const updateGlobal = useCallback((settings: Partial<ComponentAppearance>) => {
        setConfig(prev => ({
            ...prev,
            global: { ...prev.global, ...settings },
        }));
    }, []);

    // Update component-specific settings
    const updateComponent = useCallback((componentId: string, settings: Partial<ComponentAppearance>) => {
        setConfig(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentId]: { ...(prev.components[componentId] || {}), ...settings },
            },
        }));
    }, []);

    // Reset component to use global settings
    const resetComponent = useCallback((componentId: string) => {
        setConfig(prev => {
            const { [componentId]: _, ...rest } = prev.components;
            return { ...prev, components: rest };
        });
    }, []);

    // Reset all to defaults
    const resetAll = useCallback(() => {
        setConfig({ ...DEFAULT_APPEARANCE, ...defaultConfig });
    }, [defaultConfig]);

    // Convenience setters
    const setTheme = useCallback((theme: Theme) => updateGlobal({ theme }), [updateGlobal]);
    const setFontSize = useCallback((fontSize: FontSize) => updateGlobal({ fontSize }), [updateGlobal]);
    const setFontFamily = useCallback((fontFamily: FontFamily) => updateGlobal({ fontFamily }), [updateGlobal]);
    const setAccentColor = useCallback((accentColor: AccentColor) => updateGlobal({ accentColor }), [updateGlobal]);

    const value: AppearanceContextType = {
        config,
        getComponentAppearance,
        updateGlobal,
        updateComponent,
        resetComponent,
        resetAll,
        theme: config.global.theme || 'light',
        fontSize: config.global.fontSize || 'medium',
        fontFamily: config.global.fontFamily || 'system',
        accentColor: config.global.accentColor || 'shiftleft',
        setTheme,
        setFontSize,
        setFontFamily,
        setAccentColor,
        resolvedTheme,
    };

    return (
        <AppearanceContext.Provider value={value}>
            {children}
        </AppearanceContext.Provider>
    );
}

export function useAppearance() {
    const context = useContext(AppearanceContext);
    if (!context) {
        throw new Error('useAppearance must be used within an AppearanceProvider');
    }
    return context;
}
